import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import {
  PageContainer,
  DataTable,
  PremiumModal,
  Button,
} from "components/ui";
import ExportButtons from "components/ui/ExportButtons";

const JournalEntries = () => {
  const axiosPrivate = useAxiosPrivate();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedLines, setSelectedLines] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const size = 12;
  const year = new Date().getFullYear();

  const fetchEntries = async () => {
    setLoading(true);

    try {
      const res = await axiosPrivate.get(
        `/api/accountant/journal-entries?page=${page}&size=${size}`
      );

      setEntries(res.data.content);
      setTotalPages(res.data.page.totalPages);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load journal entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [page]);

  const openLines = async (id) => {
    try {
      const res = await axiosPrivate.get(
        `/api/accountant/journal-entries/${id}/lines`
      );

      setSelectedLines(res.data);
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load entry lines");
    }
  };

  return (
    <PageContainer>
      <DataTable
        title="Journal entries"
        subtitle={`Double-entry bookkeeping • ${year}`}
        toolbar={
          <ExportButtons
            path="journal-entries"
            params={{
              start: `${year}-01-01`,
              end: `${year}-12-31`,
            }}
            filename={`journal-${year}`}
          />
        }
        loading={loading}
        data={entries}
        rowKey={(e) => e.id}
        search={true}
        searchPlaceholder="Search description or reference…"
        paginate={false}
        emptyTitle="No entries yet"
        columns={[
          {
            key: "date",
            header: "Date",
            render: (e) => (
              <span className="t-mono">{e.date}</span>
            ),
          },
          {
            key: "description",
            header: "Description",
            render: (e) => e.description || "—",
          },
          {
            key: "reference",
            header: "Reference",
            render: (e) => (
              <span className="t-mono t-muted">
                {e.reference || "—"}
              </span>
            ),
          },
          {
            key: "actions",
            header: "",
            align: "right",
            render: (e) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openLines(e.id)}
              >
                View lines
              </Button>
            ),
          },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>
                Page <strong>{page + 1}</strong> of {totalPages}
              </span>

              <div
                style={{
                  display: "inline-flex",
                  gap: 6,
                }}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page === 0}
                  onClick={() =>
                    setPage((p) => Math.max(0, p - 1))
                  }
                >
                  ‹ Prev
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() =>
                    setPage((p) =>
                      Math.min(totalPages - 1, p + 1)
                    )
                  }
                >
                  Next ›
                </Button>
              </div>
            </div>
          ) : null
        }
      />

      <PremiumModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title="Journal entry lines"
        size="lg"
        footer={
          <Button
            variant="secondary"
            onClick={() => setModalOpen(false)}
          >
            Close
          </Button>
        }
      >
        <div
          className="dt-shell"
          style={{ boxShadow: "none" }}
        >
          <div className="dt-scroll">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Debit</th>
                  <th style={{ textAlign: "right" }}>Credit</th>
                </tr>
              </thead>

              <tbody>
                {selectedLines?.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <div>{line.accountName}</div>

                      <div
                        className="t-mono t-muted"
                        style={{ fontSize: 12 }}
                      >
                        {line.accountCode}
                      </div>
                    </td>

                    <td>
                      {line.description || "—"}
                    </td>

                    <td className="num">
                      {Number(line.debit || 0).toFixed(2)}
                    </td>

                    <td className="num">
                      {Number(line.credit || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

export default JournalEntries;